import { LOCALSTORAGE } from "../Config";
import { findUser, getAllUsers } from './RegisteredUser.jsx';
import { supabase } from "../supabaseClient";
import { useEffect, useState } from "react";
import Avatar from "react-avatar";
import { normalizeAvatarSrc } from "../Components/avatarUtils";
import { 
  getFriends, 
  removeFriend as removeFriendship,
  sendFriendRequest as sendFriendReq,
  acceptFriendRequest as acceptFriendReq,
  rejectFriendRequest as rejectFriendReq
} from './FriendshipHelpers.jsx';

const getFriend = (username) => {
  return { username: "", password: "" };
};

/**
 * Delete friend using new friendships table
 * Uses removeFriend from FriendshipHelpers
 */
const deleteFriend = async (friendId, currentUser, setCurrentUser) => {
  if (!friendId || !currentUser.public_user_id) {
    console.error("friendId or currentUser.public_user_id is missing!");
    return;
  }

  try {
    // Remove friendship from friendships table
    const result = await removeFriendship(currentUser.public_user_id, friendId);

    if (!result.success) {
      console.error("Error removing friend:", result.error);
      alert(result.error || "Failed to remove friend");
      return;
    }

    // Refresh friend list from friendships table
    const updatedFriends = await getFriends(currentUser.public_user_id);
    
    // Update local state
    const updatedCurrentUser = {
      ...currentUser,
      friends: updatedFriends,
    };
    setCurrentUser(updatedCurrentUser);
    alert("Friend removed successfully");
  } catch (error) {
    console.error("Error deleting friend:", error);
    alert("An unexpected error occurred");
  }
};

const RenderFriendList = ({ currentUser, setCurrentUser }) => {
  const [expandedFriendId, setExpandedFriendId] = useState(null);
  const [friendProfilesById, setFriendProfilesById] = useState({});
  const [previewAvatar, setPreviewAvatar] = useState(null);
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(true);

  const normalizeLogoValue = (value) => {
    if (typeof value !== "string") return "";
    const trimmed = value.trim();
    if (!trimmed) return "";
    const lowered = trimmed.toLowerCase();
    if (lowered === "null" || lowered === "undefined") return "";
    return trimmed;
  };

  const isLikelyImageSrc = (value) => {
    if (!value) return false;
    const normalized = value.trim().toLowerCase();
    if (!normalized) return false;

    // Accept common image source patterns only.
    return (
      normalized.startsWith("http://") ||
      normalized.startsWith("https://") ||
      normalized.startsWith("/") ||
      normalized.startsWith("data:image/") ||
      /\.(png|jpe?g|gif|webp|svg)(\?.*)?$/.test(normalized)
    );
  };

  // Fetch friends from new friendships table
  useEffect(() => {
    const loadFriends = async () => {
      if (!currentUser?.public_user_id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Use getFriends from FriendshipHelpers - returns full user objects
        const friendsList = await getFriends(currentUser.public_user_id);
        
        setFriends(friendsList);
        
        // Build profile map for expanded details
        const profileMap = friendsList.reduce((acc, friend) => {
          acc[friend.public_user_id] = friend;
          return acc;
        }, {});
        
        setFriendProfilesById(profileMap);
      } catch (error) {
        console.error("Error loading friends:", error);
      } finally {
        setLoading(false);
      }
    };

    loadFriends();
  }, [currentUser?.public_user_id]);

  const toggleExpanded = (friendId) => {
    setExpandedFriendId((prev) => (prev === friendId ? null : friendId));
  };

  if (loading) {
    return (
      <div className="friends-empty-state">
        <h1>Loading friends...</h1>
      </div>
    );
  }

  if (!friends || friends.length === 0) {
    return (
      <div className="friends-empty-state">
        <h1>Please Add Friends</h1>
      </div>
    );
  }

  return (
    <div className="friends-list">
      {friends.map((friend) => (
        <div key={friend.public_user_id} className="friends-list__row">
          {(() => {
            const friendProfile = friendProfilesById[friend.public_user_id] || friend;

            const avatarPreference =
              typeof friendProfile.avatar === "string"
                ? friendProfile.avatar.trim()
                : typeof friend.avatar === "string"
                ? friend.avatar.trim()
                : "";

            const friendAvatarSrc =
              avatarPreference === "__initials__"
                ? ""
                : normalizeAvatarSrc(friendProfile.avatar) ||
                  normalizeAvatarSrc(friend.avatar) ||
                  "";

            const favoriteTeamLogo =
              normalizeLogoValue(friendProfile.favorite_team) ||
              normalizeLogoValue(friend.favoriteTeam) ||
              normalizeLogoValue(friend.favoriteTeam?.logo) ||
              normalizeLogoValue(friend.favorite_team) ||
              normalizeLogoValue(friend.favorite_team?.logo) ||
              "";

            const safeFavoriteTeamLogo = isLikelyImageSrc(favoriteTeamLogo)
              ? favoriteTeamLogo
              : "";

            const aboutText =
              friendProfile.about_me ||
              friend.aboutMe ||
              friend.about_me ||
              `${friend.username} has not updated their about section.`;

            return (
              <>
          <div className="friends-list__meta">
            <div className="friends-list__identity">
              <button
                type="button"
                className="friends-list__avatar-btn"
                onClick={() =>
                  setPreviewAvatar({
                    name: friend.username || "Friend",
                    src: friendAvatarSrc,
                  })
                }
                aria-label={`Preview ${friend.username || "friend"} avatar`}
              >
                <Avatar
                  round={true}
                  size="34"
                  name={friend.username || "Friend"}
                  src={friendAvatarSrc}
                />
              </button>
              <h6>{friend.username}</h6>
            </div>
          </div>

          <div className="friends-list__meta">
            <p>{friend.email}</p>
          </div>

          <div className="friends-list__actions">
            {safeFavoriteTeamLogo && (
              <img
                alt={`${friend.username} favorite team`}
                src={safeFavoriteTeamLogo}
                className="friends-list__logo friends-list__logo--inline"
                onError={(event) => {
                  event.currentTarget.style.display = "none";
                }}
              />
            )}
            <button
              className="fb-btn fb-btn--ghost friends-list__toggle"
              type="button"
              onClick={() => toggleExpanded(friend.public_user_id)}
              aria-expanded={expandedFriendId === friend.public_user_id}
            >
              Details
            </button>
          </div>

          {expandedFriendId === friend.public_user_id && (
            <div className="friends-list__details">
              <div className="friends-list__about">
                <div className="friends-list__about-title">About</div>
                <p>
                  {aboutText}
                </p>
              </div>
              <button
                onClick={() =>
                  deleteFriend(friend.public_user_id, currentUser, setCurrentUser)
                }
                className="fb-btn friends-list__remove"
              >
                Remove Friend
              </button>
            </div>
          )}
              </>
            );
          })()}
        </div>
      ))}

      {previewAvatar && (
        <div
          className="friends-avatar-modal"
          onClick={() => setPreviewAvatar(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Friend avatar preview"
        >
          <div
            className="friends-avatar-modal__card"
            onClick={(event) => event.stopPropagation()}
          >
            <Avatar
              round={true}
              size="160"
              name={previewAvatar.name}
              src={previewAvatar.src}
            />
            <div className="friends-avatar-modal__name">{previewAvatar.name}</div>
            <button
              type="button"
              className="fb-btn fb-btn--ghost"
              onClick={() => setPreviewAvatar(null)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const editUser = (username, newUserObj) => {
  // TODO: search all functions that still work based on username and replace them with email
  let userFound = findUser(username);
  // Get All User Array
  const allUserArray = getAllUsers();
  let temporaryArrayUsers = allUserArray.filter(
    (user) => user.username !== userFound.username
  );
  temporaryArrayUsers.push(newUserObj);
  // Push the new array of users back to the local storage using localStorage.setItem(allUserKey, JSON.stringify(allUsers))
  localStorage.setItem(LOCALSTORAGE.USERS, JSON.stringify(temporaryArrayUsers));
};

/**
 * Send friend request using FriendshipHelpers
 */
const sendFriendRequest = async (toUserName) => {
  const { data: loggedInAuthUser, error: currentUserError } =
    await supabase.auth.getUser();

  if (currentUserError) {
    alert("Error fetching current user");
    return;
  }

  const { data: fromUser, error: fromUserError } = await supabase
    .from("users")
    .select("id")
    .eq("public_user_id", loggedInAuthUser.user.id)
    .single();

  if (fromUserError || !fromUser) {
    alert("Error fetching current user");
    return;
  }

  const { data: toUser, error: toUserError } = await supabase
    .from("users")
    .select("id")
    .eq("username", toUserName)
    .single();

  if (toUserError) {
    alert("User not found");
    return;
  }

  // Use sendFriendRequest from FriendshipHelpers
  const result = await sendFriendReq(fromUser.id, toUser.id);
  
  if (result.success) {
    alert("Friend request sent");
  } else {
    alert(result.error || "Error sending friend request");
  }
};

/**
 * Accept friend request using FriendshipHelpers
 * Creates friendship in friendships table automatically
 */
const acceptFriendRequest = async (requestId, callBack) => {
  try {
    // Get current user's internal ID
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      alert("You must be logged in");
      return;
    }

    const { data: currentUser } = await supabase
      .from("users")
      .select("id")
      .eq("public_user_id", user.id)
      .single();

    if (!currentUser) {
      alert("Error fetching user data");
      return;
    }

    // Use acceptFriendRequest from FriendshipHelpers
    // This will create the friendship AND update the request status
    const result = await acceptFriendReq(requestId, currentUser.id);

    if (!result.success) {
      console.error("Error accepting friend request:", result.error);
      alert(result.error || "Error accepting friend request");
      return;
    }

    callBack();
    alert("Friend request accepted");
  } catch (err) {
    console.error("Unexpected error:", err);
    alert("Error accepting friend request");
  }
};

/**
 * Reject friend request using FriendshipHelpers
 */
const rejectFriendRequest = async (requestId) => {
  try {
    // Get current user's internal ID
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      alert("You must be logged in");
      return;
    }

    const { data: currentUser } = await supabase
      .from("users")
      .select("id")
      .eq("public_user_id", user.id)
      .single();

    if (!currentUser) {
      alert("Error fetching user data");
      return;
    }

    // Use rejectFriendRequest from FriendshipHelpers
    const result = await rejectFriendReq(requestId, currentUser.id);

    if (!result.success) {
      console.error("Error rejecting friend request:", result.error);
      alert(result.error || "Error rejecting friend request");
      return;
    }

    alert("Friend request rejected");
  } catch (err) {
    console.error("Unexpected error:", err);
    alert("Error rejecting friend request");
  }
};

export {
  getFriend,
  sendFriendRequest,
  editUser,
  acceptFriendRequest,
  rejectFriendRequest,
  RenderFriendList,
  deleteFriend, // Export for direct use if needed
};
